import AccordionItem from 'components/common/AccordionItem';
import Modal from 'components/common/Modal/Modal';
import FormInput from 'components/FormInput';
import { useFormik } from 'formik';
import useElementSize from 'hooks/useElementSize';
import { useEffect, useState } from 'react';
import { Accordion, Button, Card, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  useCreateNewChecklistLibraryMutation,
  useDeleteChecklistsLibraryMutation,
  useDeleteSingleChecklistsMutation,
  useGetChecklistsMutation,
  useUpdateChecklistsMutation,
} from 'redux/services/checklists';
import { ChecklistItem, ChecklistLibrary } from './types';
import ChecklistActions from './ChecklistActions';
import toast from 'react-hot-toast';
import NoChecklistImage from 'assets/images/no-checklists.svg';
import { ReactComponent as PlusIcon } from 'assets/images/plus.svg';
import { useTranslation } from 'react-i18next';
import './Checklist.scss';
import { Tooltip } from '@mui/material';

const Checklist = () => {
  const { t } = useTranslation();
  const { ref, stepWrapperRef } = useElementSize();
  const [checklists, setChecklists] = useState([]);
  const [type, setType] = useState('');
  const [getChecklists, { data }] = useGetChecklistsMutation();
  const navigate = useNavigate();
  const [
    createNewChecklistLibrary,
    { isLoading: isChecklistsLibLoading, isSuccess: isChecklistsLibCreate },
  ] = useCreateNewChecklistLibraryMutation();
  const [deleteChecklistsLibrary, { isSuccess: isChecklistsLibDelete }] =
    useDeleteChecklistsLibraryMutation();
  const [deleteSingleChecklists, { isSuccess: isChecklistsDelete }] =
    useDeleteSingleChecklistsMutation();
  const [
    updateChecklists,
    { isSuccess: isChecklistLibUpdate, isLoading: isChecklistLibUpdateLoading },
  ] = useUpdateChecklistsMutation();
  const [show, setShow] = useState(false);
  const [withIcon, setWithIcon] = useState(false);
  const [libId, setLibId] = useState('');
  const [checklistId, setChecklistId] = useState('');
  const [search, setSeach] = useState('');

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    onSubmit: async (val) => {
      if (type === 'create') {
        const res: any = await createNewChecklistLibrary(val);
        if (res.data) {
          handleClose();
          toast.success('Created');
        } else toast.error(res.error.data.message);
      } else {
        const params = {
          libId: libId,
          values: val,
        };
        const res: any = await updateChecklists(params);
        if (res.data) {
          handleClose();
          toast.success('Saved');
        } else toast.error(res.error.data.message);
      }
    },
  });

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterData = data
      .filter((checklist: any) => {
        const regEx = new RegExp(e.target.value, 'i');
        return (
          checklist.name.match(regEx) ||
          checklist.checklists.some(({ name }: any) => name.match(regEx))
        );
      })
      .map((checklist: any) => {
        return {
          ...checklist,
          checklists: checklist.checklists.filter((c: any) => {
            const regEx = new RegExp(e.target.value, 'i');

            return c.name.match(regEx);
          }),
        };
      });
    setChecklists(filterData);
    setSeach(e.target.value.trim());
  };

  const deleteChecklistLib = async (id: string) => {
    const res: any = await deleteChecklistsLibrary(id);
    if (res.data) {
      toast.success('Deleted');
      handleClose();
    } else toast.error(res.error.data.message);
  };

  const deleteChecklist = async (libId: string, checklistId: string) => {
    const params = {
      libId: libId,
      checklistId: checklistId,
    };
    const res: any = await deleteSingleChecklists(params);
    if (res.data) {
      toast.success('Deleted');
      handleClose();
      setChecklistId('');
    } else toast.error(res.error.data.message);
  };

  const getAllChecklists = async () => {
    const res: any = await getChecklists('');
    if (res.data) {
      setChecklists(res.data || []);
    }
  };

  useEffect(() => {
    getAllChecklists();
  }, [
    isChecklistsLibDelete,
    isChecklistsDelete,
    isChecklistsLibCreate,
    isChecklistLibUpdate,
  ]);

  return (
    <>
      <div className="checklist-page checklist">
        <div className="pb-3 page-title" ref={ref}>
          <h2 className="mb-0">{t('checklist')}</h2>
          <p className="mb-0">{t('checklist_form_title')}</p>
        </div>
        <Card className="border-0">
          <Card.Body>
            <div className="step-wrapper" ref={stepWrapperRef}>
              <div className="d-flex mb-3 flex-column flex-sm-row">
                <div style={{ flexGrow: 1 }}>
                  <Form.Control
                    name="search_checklist"
                    placeholder={t('input_search_placeholder')}
                    onChange={handleChangeSearchInput}
                  />
                </div>
                <Button
                  variant="primary"
                  className="ms-sm-3 mt-3 mt-sm-0"
                  onClick={() => {
                    formik.resetForm();
                    setType('create');
                    setWithIcon(false);
                    setLibId('');
                    handleShow();
                  }}
                >
                  {t('label_setup_library')}
                </Button>
              </div>
              {checklists?.length > 0 ? (
                <Accordion>
                  {checklists.map((item: ChecklistLibrary, i: number) => (
                    <AccordionItem
                      eventKey={item._id}
                      text={item.name}
                      key={item._id}
                      className={search.length > 0 ? 'show' : ''}
                      leftAction={
                        <Tooltip
                          title={t('tooltip_add_checklist')}
                          placement="top"
                          arrow
                        >
                          <Button
                            variant="link"
                            size="sm"
                            id={item._id}
                            className="p-0 rounded-circle"
                            onClick={() =>
                              navigate(`/checklist/${item.libraryId}/add`)
                            }
                            style={{
                              backgroundColor: 'rgba(96, 69, 150, 0.3)',
                              width: 30,
                              height: 30,
                            }}
                          >
                            <PlusIcon width="16" />
                          </Button>
                        </Tooltip>
                      }
                      rightAction={
                        <ChecklistActions
                          hideUploadBtn
                          onEdit={() => {
                            setWithIcon(false);
                            setType('edit');
                            setLibId(item.libraryId);
                            handleShow();
                            formik.setFieldValue('name', item.name);
                          }}
                          onDelete={() => {
                            setWithIcon(true);
                            setLibId(item.libraryId);
                            formik.setFieldValue('name', item.name);
                            handleShow();
                          }}
                        />
                      }
                    >
                      {item.checklists?.length > 0 ? (
                        <ul className="ps-3 mb-0">
                          {item.checklists.map((checklist: ChecklistItem) => (
                            <li className="py-2" key={checklist._id}>
                              <div className="border-bottom d-flex justify-content-between align-items-center text-muted">
                                {checklist.name}
                                <ChecklistActions
                                  hideUploadBtn
                                  onDelete={() => {
                                    setWithIcon(true);
                                    setLibId(item.libraryId);
                                    setChecklistId(checklist._id);
                                    formik.setFieldValue(
                                      'name',
                                      checklist.name,
                                    );
                                    handleShow();
                                  }}
                                  onEdit={() =>
                                    navigate(
                                      `/checklist/${item.libraryId}/${checklist._id}`,
                                    )
                                  }
                                />
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-center py-3 text-muted mb-0">
                          {t('no_checklist')}
                        </p>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center pt-3">
                  <img alt="" src={NoChecklistImage} className="max-w-500" />
                  <h3 className="mb-0 mt-2">{t('no_active_checklist')}</h3>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>
      <Modal
        show={show}
        title={type === 'create' ? t('label_setup_library') : 'Edit Library'}
        cancelBtnText={t('btn_cancel')}
        submitBtnText={
          !withIcon
            ? type === 'create'
              ? 'Add Library'
              : t('btn_save')
            : t('btn_delete')
        }
        modalClassName="setup-library-modal"
        footerContentAlignment={withIcon ? undefined : 'center'}
        disabled={
          type === 'create'
            ? isChecklistsLibLoading
            : isChecklistLibUpdateLoading
        }
        withIcon={withIcon}
        onSubmit={
          !withIcon
            ? formik.handleSubmit
            : !checklistId
            ? () => deleteChecklistLib(libId)
            : () => deleteChecklist(libId, checklistId)
        }
        onClose={handleClose}
      >
        <FormInput
          label={t('checklist_library_name')}
          value={formik.values.name}
          name="name"
          disabled={withIcon}
          onChange={formik.handleChange}
          mb="0"
        />
      </Modal>
    </>
  );
};

export default Checklist;
